import { useEffect, useState, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { appPath, apiPath } from "../config/paths"
import { useAuth } from "../hooks/useAuth"
import { useDocuments } from "../hooks/useDocuments"
import { useToast } from "../hooks/useToast"
import Toast from "../components/Toast"
import AuthModal from "../components/AuthModal"
import TrustLoginModal from "../components/TrustLoginModal"
import SecurePdfCanvas from "../components/SecurePdfCanvas"
import TrustCenterApp from "../trust-center/TrustCenterApp"
import api from "../services/api"

async function getDocumentStreamUrl(doc, action) {
    const tokenRes = await api.post("/documents/view", {
        documentId: doc._id,
        action,
    })
    const { token, redirect } = tokenRes.data
    if (redirect) return redirect
    if (!token) throw new Error("No stream token returned from server")
    return apiPath(`/documents/stream/${token}`)
}

/** Fetches PDF bytes for the canvas viewer (consumes the one-time stream token). */
async function fetchDocumentArrayBuffer(doc) {
    const tokenRes = await api.post("/documents/view", {
        documentId: doc._id,
        action: "view",
    })
    const { token, redirect } = tokenRes.data

    if (redirect) {
        const r = await fetch(redirect)
        if (!r.ok) throw new Error("Failed to load document")
        return r.arrayBuffer()
    }
    if (!token) throw new Error("No stream token returned from server")

    const streamUrl = apiPath(`/documents/stream/${token}`)
    const streamRes = await api.get(streamUrl, { responseType: "arraybuffer" })
    const ctype = streamRes.headers["content-type"] || ""
    if (ctype.includes("application/json")) {
        const text = new TextDecoder().decode(new Uint8Array(streamRes.data))
        const json = JSON.parse(text)
        throw new Error(json.error || "Failed to load document")
    }
    return streamRes.data
}

async function downloadDocument(doc) {
    const streamUrl = await getDocumentStreamUrl(doc, "download")
    if (streamUrl.startsWith("http")) {
        const link = document.createElement("a")
        link.href = streamUrl
        link.setAttribute("download", `${doc.title || "document"}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        return
    }
    const streamRes = await api.get(streamUrl, { responseType: "blob" })
    const contentType = streamRes.headers["content-type"] || ""
    if (contentType.includes("application/json")) {
        const text = await streamRes.data.text()
        const json = JSON.parse(text)
        if (json.error) throw new Error(json.error)
    }
    const blob = new Blob([streamRes.data], { type: "application/pdf" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", `${doc.title || "document"}.pdf`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 5000)
}

function DocumentViewer({ title, data, onClose }) {
    useEffect(() => {
        const k = (e) => e.key === "Escape" && onClose()
        window.addEventListener("keydown", k)
        return () => window.removeEventListener("keydown", k)
    }, [onClose])

    return (
        <div className="doc-viewer-overlay" onClick={onClose}>
            <aside
                className="doc-viewer-drawer"
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(e) => e.stopPropagation()}
            >
                <header className="doc-viewer-drawer__head">
                    <h3 className="doc-viewer-drawer__title">{title}</h3>
                    <button type="button" className="doc-viewer-drawer__close" onClick={onClose} aria-label="Close">&times;</button>
                </header>
                {/* Rendered to canvas (never an <iframe> to the raw PDF) so the browser's
                    native PDF viewer — and its right-click Print/Save menu — never appears. */}
                <div className="doc-viewer-drawer__body">
                    <SecurePdfCanvas data={data} />
                </div>
            </aside>
        </div>
    )
}

export default function Home() {
    const navigate = useNavigate()
    const { toasts, toast, removeToast } = useToast()

    const toastRef = useRef(toast)
    useEffect(() => { toastRef.current = toast }, [toast])

    const onSessionReplaced = useCallback(() => {
        toastRef.current.error("Your session has expired because you logged in from another device.")
        setTimeout(() => { window.location.href = appPath("/") }, 2500)
    }, [])

    const { user, setUserAndCache, handleLogout } = useAuth({ onSessionReplaced })
    const { documents, widgets, controls, loadData } = useDocuments()

    const [myRequests, setMyRequests] = useState([])
    const [isLoginOpen, setIsLoginOpen] = useState(false)
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(false)
    const [selectedDocs, setSelectedDocs] = useState([])
    const [viewer, setViewer] = useState(null)
    const [downloadingDocId, setDownloadingDocId] = useState(null)

    const userId = user?._id ?? null

    useEffect(() => { loadData() }, [loadData])

    const loadMyRequests = useCallback(async () => {
        if (!userId) {
            setMyRequests([])
            return
        }
        try {
            const requestsRes = await api.get("/documents/my-requests")
            setMyRequests(requestsRes.data)
        } catch {
            setMyRequests([])
        }
    }, [userId])

    useEffect(() => { loadMyRequests() }, [loadMyRequests])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const docId = params.get("docId")
        const docIds = params.get("docIds")
        const step = params.get("step")
        if (step === "4" && (docId || docIds)) {
            setIsRequestModalOpen(true)
        }
    }, [])

    const openRequestModal = (docs) => {
        const docArray = Array.isArray(docs) ? docs : [docs]
        setSelectedDocs(docArray)
        if (user) {
            if (user.role === "prospect" && !user.ndaSigned) {
                const ids = docArray.map((d) => d._id).join(",")
                navigate(`/nda?redirect=home&docIds=${ids}`, { replace: true })
                return
            }
        } else {
            setIsLoginOpen(true)
            return
        }
        setIsRequestModalOpen(true)
    }

    const openLoginModal = (doc) => {
        setSelectedDocs(doc ? [doc] : [])
        setIsLoginOpen(true)
    }

    const handleAuthSuccess = (userData) => {
        setUserAndCache(userData)
        loadData()
        loadMyRequests()
    }

    const handleDownload = async (doc) => {
        const docId = doc?._id
        if (!docId || downloadingDocId) return
        setDownloadingDocId(docId)
        try {
            await downloadDocument(doc)
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || "Failed to download document")
        } finally {
            setDownloadingDocId(null)
        }
    }

    const handleView = async (doc) => {
        const title = doc.title || "Document"
        // Open the viewer immediately with a spinner instead of waiting for the
        // (potentially slow) download to finish before showing anything.
        setViewer({ title, data: null })
        try {
            const data = await fetchDocumentArrayBuffer(doc)
            setViewer({ title, data })
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || "Failed to open document")
            setViewer(null)
        }
    }

    return (
        <>
            <Toast toasts={toasts} onRemove={removeToast} />
            <TrustCenterApp
                documents={documents}
                widgets={widgets}
                controls={controls}
                user={user}
                myRequests={myRequests}
                onLogin={() => openLoginModal()}
                onLogout={handleLogout}
                onRequestAccess={openRequestModal}
                onDownload={handleDownload}
                downloadingDocId={downloadingDocId}
                onView={handleView}
                onLoginRequired={openLoginModal}
            />
            <TrustLoginModal
                isOpen={isLoginOpen}
                onClose={() => setIsLoginOpen(false)}
                onAuthSuccess={handleAuthSuccess}
            />
            <AuthModal
                isOpen={isRequestModalOpen}
                onClose={() => setIsRequestModalOpen(false)}
                selectedDocs={selectedDocs}
                onAuthSuccess={handleAuthSuccess}
                toast={toast}
                user={user}
                onRequestSent={loadMyRequests}
            />
            {viewer && (
                <DocumentViewer
                    title={viewer.title}
                    data={viewer.data}
                    onClose={() => setViewer(null)}
                />
            )}
        </>
    )
}
